import { Component, OnInit, OnDestroy, ViewChild, HostListener, ElementRef } from '@angular/core';
import { APP_VERSION } from './app-version';
import { CommonModule, AsyncPipe } from '@angular/common';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { MatSidenavModule, MatSidenav } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { AuthService } from './auth/auth.service';
import { TenantService } from './core/tenant.service';
import { ApiService } from './core/api.service';
import { LifecycleService } from './core/lifecycle.service';
import { FeaturesService } from './core/features.service';
import { NotificationsService } from './core/notifications.service';
import { NotificationPanelComponent } from './shared/notification-panel/notification-panel.component';
import { FeatureGateDirective } from './shared/feature-gate.directive';
import { ClientsService } from './clients/clients.service';
import { EmployeesService } from './employees/employees.service';
import { ServiceOrdersService } from './service-orders/service-orders.service';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription, Subject, filter, debounceTime, switchMap, forkJoin, of } from 'rxjs';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule, AsyncPipe,
    RouterOutlet, RouterLink, RouterLinkActive,
    MatSidenavModule, MatListModule,
    MatIconModule, MatButtonModule, MatTooltipModule, MatMenuModule,
    MatBadgeModule, MatDividerModule,
    FeatureGateDirective,
    NotificationPanelComponent,
  ],
  template: `
    <ng-container *ngIf="auth.isLoggedIn; else bare">
    <mat-sidenav-container class="app-container" [autosize]="true">

      <mat-sidenav #sidenav [mode]="sidenavMode" [opened]="sidenavOpened"
        (closedStart)="sidenavOpened = false" class="sidenav"
        [class.collapsed]="sidenavCollapsed && sidenavMode === 'side'">

        <!-- Brand -->
        <div class="brand">
          <img src="/logo-icon.svg" class="brand-logo-img" alt="SchedulerPro LLC" width="38" height="38">
          <div class="brand-text">
            <span class="brand-name">{{ tenant.companyName }}</span>
            <span class="brand-tagline">SchedulerPro LLC</span>
          </div>
        </div>

        <!-- Navigation -->
        <nav class="nav">

          <!-- Home -->
          <a class="nav-item nav-home" routerLink="/dashboard" routerLinkActive="active" [routerLinkActiveOptions]="{exact: true}" (click)="navClick()" matTooltip="Today" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>wb_sunny</mat-icon></span>
            <span>Today</span>
          </a>

          <p class="nav-label">People</p>
          <a class="nav-item nav-green" routerLink="/clients" routerLinkActive="active" (click)="navClick()" matTooltip="Clients" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>people</mat-icon></span>
            <span>Clients</span>
          </a>
          <a class="nav-item nav-blue" routerLink="/employees" routerLinkActive="active" (click)="navClick()" matTooltip="Employees" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>badge</mat-icon></span>
            <span>Employees</span>
          </a>

          <p class="nav-label">Authorizations</p>
          <a class="nav-item nav-amber" routerLink="/case-managers" routerLinkActive="active" (click)="navClick()" matTooltip="Case Managers" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>manage_accounts</mat-icon></span>
            <span>Case Managers</span>
          </a>
          <a class="nav-item nav-amber" routerLink="/providers" routerLinkActive="active" (click)="navClick()" matTooltip="Providers" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>business</mat-icon></span>
            <span>Providers</span>
          </a>
          <a class="nav-item nav-amber" routerLink="/service-orders" routerLinkActive="active" (click)="navClick()" matTooltip="Service Orders" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>assignment</mat-icon></span>
            <span>Service Orders</span>
          </a>

          <p class="nav-label">Scheduling</p>
          <a class="nav-item nav-purple" routerLink="/absences" routerLinkActive="active" (click)="navClick()" matTooltip="Absences" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>event_busy</mat-icon></span>
            <span>Absences</span>
          </a>
          <a class="nav-item nav-purple" routerLink="/schedules/appointments" routerLinkActive="active" *ngIf="auth.canWrite()" (click)="navClick()" matTooltip="Appointments" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>local_hospital</mat-icon></span>
            <span>Appointments</span>
          </a>
          <a class="nav-item nav-purple" routerLink="/schedules/builder" routerLinkActive="active" *ngIf="auth.canWrite()" (click)="navClick()" matTooltip="Schedule Builder" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>build</mat-icon></span>
            <span>Schedule Builder</span>
          </a>
          <a class="nav-item nav-purple" routerLink="/schedules/monitor" routerLinkActive="active" (click)="navClick()" matTooltip="Schedule Monitor" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>view_week</mat-icon></span>
            <span>Schedule Monitor</span>
          </a>
          <a class="nav-item nav-purple" routerLink="/shift-broadcasts" routerLinkActive="active" (click)="navClick()" *featureGate="'shift_broadcast'" matTooltip="Shift Broadcasts" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>cell_tower</mat-icon></span>
            <span>Shift Broadcasts</span>
            <span class="nav-badge bg-purple" *ngIf="openBroadcastCount">{{ openBroadcastCount }}</span>
          </a>

          <p class="nav-label">Time Tracking</p>
          <a class="nav-item nav-teal" routerLink="/time-slips/overtime" routerLinkActive="active" (click)="navClick()" matTooltip="Overtime Monitor" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>trending_up</mat-icon></span>
            <span>Overtime Monitor</span>
          </a>
          <a class="nav-item nav-teal" routerLink="/time-slips/timesheets" routerLinkActive="active" (click)="navClick()" matTooltip="Timesheets" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>fact_check</mat-icon></span>
            <span>Timesheets</span>
            <span class="nav-badge bg-teal" *ngIf="pendingTimesheetCount">{{ pendingTimesheetCount }}</span>
          </a>

          <p class="nav-label">Reports &amp; AI</p>
          <a class="nav-item nav-indigo" routerLink="/ai-skills" routerLinkActive="active" *ngIf="auth.canWrite() && features.hasFeature('ai_skills')" (click)="navClick()" matTooltip="AI Skills" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>auto_awesome</mat-icon></span>
            <span>AI Skills</span>
          </a>
          <span class="nav-item nav-indigo nav-gated" *ngIf="auth.canWrite() && !features.hasFeature('ai_skills')" matTooltip="AI Skills" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>auto_awesome</mat-icon></span>
            <span>AI Skills</span>
            <span class="tier-badge">Pro</span>
          </span>
          <a class="nav-item nav-orange" routerLink="/reports" routerLinkActive="active" [routerLinkActiveOptions]="{exact: true}" (click)="navClick()" matTooltip="Reports" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>bar_chart</mat-icon></span>
            <span>Reports</span>
          </a>

          <p class="nav-label" *ngIf="auth.isAdmin()">Administration</p>
          <a class="nav-item nav-slate" routerLink="/account" routerLinkActive="active" *ngIf="auth.isOwner()" (click)="navClick()" matTooltip="Account" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>account_balance_wallet</mat-icon></span>
            <span>Account</span>
          </a>
          <a class="nav-item nav-slate" routerLink="/audit-log" routerLinkActive="active" *ngIf="auth.isAdmin()" (click)="navClick()" matTooltip="Audit Log" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>history</mat-icon></span>
            <span>Audit Log</span>
          </a>
          <a class="nav-item nav-slate" routerLink="/settings" routerLinkActive="active" *ngIf="auth.isAdmin()" (click)="navClick()" matTooltip="Settings" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>settings</mat-icon></span>
            <span>Settings</span>
          </a>
          <a class="nav-item nav-slate" routerLink="/users" routerLinkActive="active" *ngIf="auth.isAdmin()" (click)="navClick()" matTooltip="Users" matTooltipPosition="right" [matTooltipDisabled]="!railCollapsed">
            <span class="nav-icon-wrap"><mat-icon>manage_accounts</mat-icon></span>
            <span>Users</span>
          </a>
        </nav>

        <!-- Footer -->
        <div class="sidenav-footer">
          <span class="version-chip">Version: {{ appVersion }}</span>
        </div>

      </mat-sidenav>

      <mat-sidenav-content class="shell-content">
        <header class="topbar">
          <div class="topbar-left">
            <button mat-icon-button class="hamburger-btn" (click)="toggleSidenav()">
              <mat-icon>menu</mat-icon>
            </button>
            <div class="breadcrumb">
              <ng-container *ngIf="currentPageName !== 'Today'"><a routerLink="/dashboard" class="bc-home">Today</a>
              <span class="bc-sep">/</span></ng-container>
              <span class="bc-current">{{ currentPageName }}</span>
            </div>
            <div class="global-search" [class.has-results]="searchResults.length > 0 || searchQuery.length >= 2"
              (click)="searchInput.focus()">
              <mat-icon>search</mat-icon>
              <input #searchInput placeholder="Search clients, employees, orders..."
                [value]="searchQuery"
                (input)="onSearchInput($event)"
                (focus)="searchFocused = true"
                (keydown.escape)="clearSearch()"
                (keydown.arrowdown)="onArrowDown($event)"
                (keydown.arrowup)="onArrowUp($event)"
                (keydown.enter)="onSearchEnter()" />
              <span class="search-shortcut" *ngIf="searchQuery.length === 0">&#8984;K</span>
              <button class="search-clear" *ngIf="searchQuery.length > 0" (click)="clearSearch()">
                <mat-icon>close</mat-icon>
              </button>
            </div>
            <div class="search-overlay" *ngIf="searchFocused && searchQuery.length >= 2" (click)="clearSearch()"></div>
            <div class="search-dropdown" *ngIf="searchFocused && searchQuery.length >= 2">
              <div *ngIf="searchLoading" class="search-loading">Searching…</div>
              <div *ngIf="!searchLoading && searchResults.length === 0" class="search-empty">No results found</div>
              <div *ngFor="let r of searchResults; let i = index"
                class="search-result" [class.highlighted]="i === searchHighlight"
                (click)="goToResult(r)" (mouseenter)="searchHighlight = i">
                <div class="sr-icon" [class]="r.category">
                  <mat-icon>{{ r.icon }}</mat-icon>
                </div>
                <div class="sr-content">
                  <div class="sr-title">{{ r.title }}</div>
                  <div class="sr-sub">{{ r.subtitle }}</div>
                </div>
                <span class="sr-cat-label">{{ r.categoryLabel }}</span>
              </div>
            </div>
          </div>
          <div class="topbar-right">
            <div class="notif-wrapper">
              <button mat-icon-button class="topbar-btn" matTooltip="Notifications"
                (click)="toggleNotifPanel($event)">
                <mat-icon>{{ (notifSvc.unreadCount$ | async) ? 'notifications_active' : 'notifications_none' }}</mat-icon>
                <span class="notif-badge" *ngIf="(notifSvc.unreadCount$ | async) as count">{{ count > 99 ? '99+' : count }}</span>
              </button>
              <app-notification-panel *ngIf="notifPanelOpen"
                class="notif-panel-dropdown"
                (closed)="notifPanelOpen = false">
              </app-notification-panel>
            </div>
            <button mat-icon-button class="topbar-btn" matTooltip="Help" (click)="openHelp()">
              <mat-icon>help_outline</mat-icon>
            </button>
            <div class="topbar-divider"></div>
            <div class="user-pill" [matMenuTriggerFor]="userMenu">
              <div class="user-avatar">{{ userInitials }}</div>
              <div class="user-pill-info">
                <span class="user-pill-name">{{ (auth.currentUser$ | async)?.name }}</span>
                <span [class]="'user-pill-role role-' + ((auth.currentUser$ | async)?.role)">{{ (auth.currentUser$ | async)?.role }}</span>
              </div>
            </div>
            <mat-menu #userMenu="matMenu" class="user-menu-panel" xPosition="before">
              <div class="menu-user-header" (click)="$event.stopPropagation()">
                <div class="menu-avatar">{{ userInitials }}</div>
                <div class="menu-user-info">
                  <span class="menu-user-name">{{ (auth.currentUser$ | async)?.name }}</span>
                  <span [class]="'menu-role-chip role-' + ((auth.currentUser$ | async)?.role)">{{ (auth.currentUser$ | async)?.role }}</span>
                </div>
              </div>
              <mat-divider></mat-divider>
              <button mat-menu-item routerLink="/profile">
                <mat-icon>manage_accounts</mat-icon>
                <span>My Profile</span>
              </button>
              <button mat-menu-item (click)="openHelp()">
                <mat-icon>help_outline</mat-icon>
                <span>Help &amp; Documentation</span>
              </button>
              <ng-container *ngIf="auth.isAdmin()">
                <mat-divider></mat-divider>
                <p class="menu-section-label">Administration</p>
                <button mat-menu-item routerLink="/account" *ngIf="auth.isOwner()">
                  <mat-icon>account_balance_wallet</mat-icon>
                  <span>Account</span>
                </button>
                <button mat-menu-item routerLink="/audit-log">
                  <mat-icon>history</mat-icon>
                  <span>Audit Log</span>
                </button>
                <button mat-menu-item routerLink="/settings">
                  <mat-icon>settings</mat-icon>
                  <span>Settings</span>
                </button>
                <button mat-menu-item routerLink="/users">
                  <mat-icon>manage_accounts</mat-icon>
                  <span>Users</span>
                </button>
              </ng-container>
              <mat-divider></mat-divider>
              <button mat-menu-item class="menu-signout" (click)="auth.logout()">
                <mat-icon>logout</mat-icon>
                <span>Sign out</span>
              </button>
            </mat-menu>
          </div>
        </header>
        <!-- Urgent notification banner -->
        <div class="urgent-alert-banner" *ngIf="currentPageName !== 'Today' && (notifSvc.urgentAlerts$ | async)?.length as alertCount">
          <mat-icon>warning</mat-icon>
          <span>
            <strong>{{ alertCount }}</strong> urgent notification{{ alertCount > 1 ? 's' : '' }} requiring attention
          </span>
          <button mat-flat-button class="urgent-view-btn" (click)="toggleNotifPanel($event)">View</button>
        </div>

        <!-- Trial expiry banner -->
        <div *ngIf="showTrialBanner" class="trial-banner" [class.trial-urgent]="trialDaysLeft <= 3">
          <mat-icon class="trial-banner-icon">{{ trialDaysLeft <= 3 ? 'warning' : 'schedule' }}</mat-icon>
          <span *ngIf="trialDaysLeft > 1">
            <strong>{{ trialDaysLeft }} days left</strong> in your free trial (ends {{ trialEndDate }}).
          </span>
          <span *ngIf="trialDaysLeft === 1">
            <strong>Last day</strong> of your free trial — your workspace will be locked tomorrow.
          </span>
          <span *ngIf="trialDaysLeft <= 0">
            <strong>Your trial has expired.</strong> Upgrade now to keep your workspace.
          </span>
          <button mat-flat-button class="trial-upgrade-btn" *ngIf="auth.isOwner()" (click)="goToUpgrade()">
            Upgrade Now
          </button>
          <button mat-icon-button class="trial-banner-close" (click)="dismissTrialBanner()">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <div class="offline-banner" *ngIf="isOffline">
          <mat-icon>wifi_off</mat-icon>
          <span>You're offline — some features may not work until your connection is restored.</span>
        </div>

        <main class="page-content">
          <router-outlet></router-outlet>
        </main>
      </mat-sidenav-content>

    </mat-sidenav-container>
    </ng-container>
    <ng-template #bare><router-outlet></router-outlet></ng-template>
  `,
  styles: [`
    /* ── Shell ─────────────────────────────────────── */
    .app-container { height: 100vh; }

    /* ── Sidebar ────────────────────────────────────── */
    .sidenav {
      width: 244px;
      background: #0F172A;
      color: white;
      border-right: none;
      box-shadow: 4px 0 32px rgba(15, 23, 42, 0.30);
      display: flex;
      flex-direction: column;
      transition: width 0.2s ease;
    }

    /* Collapsed icon-only rail (desktop) — toggled by the hamburger */
    .sidenav.collapsed { width: 64px; }
    .sidenav.collapsed .brand-text,
    .sidenav.collapsed .nav-label,
    .sidenav.collapsed .version-chip,
    .sidenav.collapsed .nav-item > span:not(.nav-icon-wrap) { display: none; }
    .sidenav.collapsed .brand,
    .sidenav.collapsed .nav-item,
    .sidenav.collapsed .sidenav-footer { justify-content: center; padding-left: 0; padding-right: 0; }
    .sidenav.collapsed .nav { overflow-x: hidden; }

    /* Brand */
    .brand {
      display: flex;
      align-items: center;
      gap: 11px;
      padding: 22px 18px 18px;
      border-bottom: 1px solid rgba(255,255,255,0.06);
      flex-shrink: 0;
    }
    .brand-logo-img {
      display: block;
      flex-shrink: 0;
      border-radius: 9px;
    }
    .brand-text { display: flex; flex-direction: column; gap: 1px; }
    .brand-name { font-size: 13.5px; font-weight: 800; color: #F8FAFC; letter-spacing: -0.3px; }
    .brand-tagline { font-size: 10px; color: rgba(255,255,255,0.38); letter-spacing: 0.4px; }

    /* Nav */
    .nav {
      padding: 10px 10px;
      flex: 1;
      overflow-y: auto;
    }

    .nav-label {
      margin: 14px 8px 5px;
      font-size: 9.5px;
      font-weight: 800;
      letter-spacing: 1.4px;
      text-transform: uppercase;
      color: rgba(255,255,255,0.28);
    }

    .nav-item {
      display: flex;
      align-items: center;
      gap: 9px;
      padding: 8px 10px;
      border-radius: 9px;
      margin-bottom: 1px;
      text-decoration: none;
      color: rgba(255,255,255,0.60);
      font-size: 12px;
      font-weight: 500;
      transition: background 0.13s, color 0.13s;
      cursor: pointer;
    }
    .nav-item:hover {
      background: rgba(255,255,255,0.07);
      color: rgba(255,255,255,0.88);
    }
    /* Default active (teal — matches marketing site primary) */
    .nav-item.active {
      background: rgba(13,148,136,0.18);
      color: #5EEAD4;
    }
    .nav-item.active .nav-icon-wrap { background: rgba(13,148,136,0.28); }
    .nav-item.active .nav-icon-wrap mat-icon { color: #2DD4BF; }

    /* Home */
    .nav-home.active {
      background: rgba(255,255,255,0.10);
      color: #F8FAFC;
    }
    .nav-home.active .nav-icon-wrap { background: rgba(255,255,255,0.15); }
    .nav-home.active .nav-icon-wrap mat-icon { color: #F8FAFC; }
    .nav-home:hover .nav-icon-wrap mat-icon { color: #F8FAFC; }

    /* People — Green */
    .nav-green.active {
      background: rgba(22,163,74,0.18);
      color: #86EFAC;
    }
    .nav-green.active .nav-icon-wrap { background: rgba(22,163,74,0.28); }
    .nav-green.active .nav-icon-wrap mat-icon { color: #4ADE80; }
    .nav-green:hover .nav-icon-wrap mat-icon { color: #86EFAC; }

    /* Scheduling — Teal */
    .nav-blue.active {
      background: rgba(30,64,175,0.18);
      color: #93C5FD;
    }
    .nav-blue.active .nav-icon-wrap { background: rgba(30,64,175,0.28); }
    .nav-blue.active .nav-icon-wrap mat-icon { color: #60A5FA; }
    .nav-blue:hover .nav-icon-wrap mat-icon { color: #93C5FD; }

    /* Time Tracking — Teal */
    .nav-teal.active {
      background: rgba(13,148,136,0.18);
      color: #5EEAD4;
    }
    .nav-teal.active .nav-icon-wrap { background: rgba(13,148,136,0.28); }
    .nav-teal.active .nav-icon-wrap mat-icon { color: #2DD4BF; }
    .nav-teal:hover .nav-icon-wrap mat-icon { color: #5EEAD4; }

    /* Authorizations — Amber */
    .nav-amber.active {
      background: rgba(217,119,6,0.18);
      color: #FCD34D;
    }
    .nav-amber.active .nav-icon-wrap { background: rgba(217,119,6,0.28); }
    .nav-amber.active .nav-icon-wrap mat-icon { color: #FCD34D; }
    .nav-amber:hover .nav-icon-wrap mat-icon { color: #FDE68A; }

    /* Records — Purple */
    .nav-purple.active {
      background: rgba(124,58,237,0.18);
      color: #C4B5FD;
    }
    .nav-purple.active .nav-icon-wrap { background: rgba(124,58,237,0.28); }
    .nav-purple.active .nav-icon-wrap mat-icon { color: #A78BFA; }
    .nav-purple:hover .nav-icon-wrap mat-icon { color: #C4B5FD; }

    /* Reports — Orange */
    .nav-orange.active {
      background: rgba(234,88,12,0.18);
      color: #FDBA74;
    }
    .nav-orange.active .nav-icon-wrap { background: rgba(234,88,12,0.28); }
    .nav-orange.active .nav-icon-wrap mat-icon { color: #FB923C; }
    .nav-orange:hover .nav-icon-wrap mat-icon { color: #FDBA74; }

    /* AI Skills — Indigo */
    .nav-indigo.active {
      background: rgba(67,56,202,0.20);
      color: #C7D2FE;
    }
    .nav-indigo.active .nav-icon-wrap { background: rgba(67,56,202,0.30); }
    .nav-indigo.active .nav-icon-wrap mat-icon { color: #A5B4FC; }
    .nav-indigo:hover .nav-icon-wrap mat-icon { color: #C7D2FE; }

    /* Administration — Slate */
    .nav-slate.active {
      background: rgba(100,116,139,0.18);
      color: #CBD5E1;
    }
    .nav-slate.active .nav-icon-wrap { background: rgba(100,116,139,0.28); }
    .nav-slate.active .nav-icon-wrap mat-icon { color: #94A3B8; }
    .nav-slate:hover .nav-icon-wrap mat-icon { color: #CBD5E1; }

    .nav-icon-wrap {
      width: 28px; height: 28px;
      border-radius: 7px;
      display: flex; align-items: center; justify-content: center;
      background: rgba(255,255,255,0.05);
      flex-shrink: 0;
      transition: background 0.13s;
    }
    .nav-item:hover .nav-icon-wrap { background: rgba(255,255,255,0.10); }
    .nav-item.active .nav-icon-wrap { background: rgba(13,148,136,0.30); }
    .nav-icon-wrap mat-icon { font-size: 17px; width: 17px; height: 17px; color: rgba(255,255,255,0.50); }

    .nav-badge {
      margin-left: auto;
      font-size: 10px;
      font-weight: 700;
      padding: 1px 7px;
      border-radius: 10px;
      line-height: 16px;
      color: #fff;
    }
    .nav-badge.bg-purple { background: rgba(124,58,237,0.25); color: #C4B5FD; }
    .nav-badge.bg-teal { background: rgba(13,148,136,0.25); color: #5EEAD4; }

    .nav-gated {
      opacity: 0.45;
      cursor: default;
    }
    .nav-gated:hover {
      background: transparent;
      color: rgba(255,255,255,0.60);
    }
    .tier-badge {
      margin-left: auto;
      font-size: 9px;
      font-weight: 700;
      padding: 1px 6px;
      border-radius: 8px;
      line-height: 15px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      background: rgba(124,58,237,0.30);
      color: #C4B5FD;
    }

    /* Footer */
    .sidenav-footer {
      padding: 12px 18px;
      border-top: 1px solid rgba(255,255,255,0.05);
      display: flex;
      align-items: center;
      gap: 10px;
      flex-shrink: 0;
    }
    .version-chip {
      background: rgba(13,148,136,0.25);
      color: #5EEAD4;
      border-radius: 20px;
      padding: 2px 9px;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.5px;
    }
    /* ── Top bar ────────────────────────────────────── */
    .topbar {
      height: 48px;
      padding: 0 24px;
      background: #FFFFFF;
      border-bottom: 1px solid #E2E8F0;
      box-shadow: 0 1px 6px rgba(15,23,42,0.04);
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: sticky;
      top: 0;
      z-index: 10;
      flex-shrink: 0;
    }
    .topbar-left {
      display: flex;
      align-items: center;
      gap: 20px;
    }
    .breadcrumb {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: #94A3B8;
    }
    .bc-home {
      cursor: pointer;
      text-decoration: none;
      color: #94A3B8;
      transition: color 0.15s;
    }
    .bc-home:hover { color: #0D9488; }
    .bc-sep { font-size: 14px; color: #CBD5E1; }
    .bc-current { color: #0F172A; font-weight: 600; }
    .global-search {
      position: relative;
      display: flex;
      align-items: center;
      gap: 8px;
      background: #F1F5F9;
      border: 1px solid #E2E8F0;
      border-radius: 10px;
      padding: 7px 14px;
      width: 300px;
      transition: all 0.2s;
      cursor: text;
      z-index: 999;
    }
    .global-search:focus-within {
      border-color: #0D9488;
      background: #fff;
      box-shadow: 0 0 0 3px rgba(13,148,136,0.08);
    }
    .global-search mat-icon { font-size: 18px; width: 18px; height: 18px; color: #94A3B8; }
    .global-search input {
      border: none;
      background: none;
      outline: none;
      font-family: inherit;
      font-size: 12px;
      color: #1E293B;
      width: 100%;
    }
    .global-search input::placeholder { color: #94A3B8; }
    .search-shortcut {
      background: #E2E8F0;
      color: #64748B;
      font-size: 10px;
      font-weight: 600;
      padding: 2px 6px;
      border-radius: 4px;
      white-space: nowrap;
    }
    .search-clear {
      background: none; border: none; cursor: pointer; padding: 0;
      display: flex; align-items: center;
    }
    .search-clear mat-icon { font-size: 16px; width: 16px; height: 16px; color: #94A3B8; }
    .search-clear:hover mat-icon { color: #64748B; }
    .global-search.has-results:focus-within {
      border-radius: 10px 10px 0 0;
    }
    .search-overlay {
      position: fixed; inset: 0; z-index: 998; background: rgba(0,0,0,0.15);
    }
    .search-dropdown {
      position: absolute; top: 100%; left: 0; right: 0;
      z-index: 999; background: #fff;
      border: 1px solid #E2E8F0; border-top: none;
      border-radius: 0 0 10px 10px;
      box-shadow: 0 8px 24px rgba(15,23,42,0.12);
      max-height: 360px; overflow-y: auto;
    }
    .search-loading, .search-empty {
      padding: 16px 18px; font-size: 12px; color: #94A3B8; text-align: center;
    }
    .search-result {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 16px; cursor: pointer; transition: background 0.1s;
    }
    .search-result.highlighted { background: #F1F5F9; }
    .sr-icon {
      width: 32px; height: 32px; border-radius: 8px;
      display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    }
    .sr-icon mat-icon { font-size: 16px; width: 16px; height: 16px; }
    .sr-icon.client  { background: #F0FDF4; }
    .sr-icon.client  mat-icon { color: #16A34A; }
    .sr-icon.employee { background: #EFF6FF; }
    .sr-icon.employee mat-icon { color: #1E40AF; }
    .sr-icon.so       { background: #FFFBEB; }
    .sr-icon.so       mat-icon { color: #D97706; }
    .sr-content { flex: 1; min-width: 0; }
    .sr-title { font-size: 12px; font-weight: 600; color: #0F172A; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .sr-sub { font-size: 11px; color: #94A3B8; }
    .sr-cat-label {
      font-size: 10px; font-weight: 600; text-transform: uppercase;
      letter-spacing: 0.3px; color: #94A3B8; white-space: nowrap;
    }
    .topbar-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .topbar-btn {
      width: 36px;
      height: 36px;
      position: relative;
    }
    .topbar-btn mat-icon { color: #64748B; }
    .topbar-btn:hover mat-icon { color: #0F172A; }
    .notif-wrapper { position: relative; }
    .notif-badge {
      position: absolute;
      top: 0;
      right: -2px;
      min-width: 18px;
      height: 18px;
      line-height: 18px;
      border-radius: 9px;
      background: #EF4444;
      color: #fff;
      font-size: 10px;
      font-weight: 700;
      text-align: center;
      padding: 0 4px;
      border: 2px solid #fff;
      pointer-events: none;
      z-index: 1;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }
    .notif-panel-dropdown {
      position: absolute;
      top: 44px;
      right: 0;
      z-index: 1000;
    }
    .topbar-divider {
      width: 1px;
      height: 28px;
      background: #E2E8F0;
      margin: 0 4px;
    }
    .user-pill {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 4px 10px 4px 4px;
      border-radius: 24px;
      cursor: pointer;
      transition: background 0.15s;
    }
    .user-pill:hover { background: #F1F5F9; }
    .user-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #0D9488;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 700;
      flex-shrink: 0;
    }
    .user-pill-info { display: flex; flex-direction: column; }
    .user-pill-name { font-size: 12px; font-weight: 600; color: #0F172A; line-height: 1.2; }
    .user-pill-role {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .role-admin { color: #0D9488; }
    .role-scheduler { color: #16A34A; }
    .role-readonly { color: #64748B; }

    /* ── Urgent notification banner ────────────────── */
    .urgent-alert-banner {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 24px;
      background: #FEE2E2; border-bottom: 1px solid #FCA5A5;
      font-size: 12px; color: #991B1B; flex-shrink: 0;
    }
    .urgent-alert-banner mat-icon { font-size: 18px; width: 18px; height: 18px; color: #DC2626; flex-shrink: 0; }
    .urgent-alert-banner strong { font-weight: 700; }
    .urgent-view-btn {
      margin-left: auto;
      background: #DC2626 !important; color: white !important;
      font-size: 12px; height: 30px; line-height: 30px; flex-shrink: 0;
    }

    /* ── Trial banner ───────────────────────────────── */
    .trial-banner {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 24px;
      background: #FEF3C7;
      border-bottom: 1px solid #FCD34D;
      font-size: 12px;
      color: #92400E;
      flex-shrink: 0;
    }
    .trial-banner-icon { font-size: 18px; width: 18px; height: 18px; color: #D97706; flex-shrink: 0; }
    .trial-banner strong { font-weight: 700; }
    .trial-upgrade-btn {
      margin-left: auto;
      background: #D97706 !important;
      color: white !important;
      font-size: 12px;
      height: 30px;
      line-height: 30px;
      flex-shrink: 0;
    }
    .trial-banner-close { flex-shrink: 0; color: #92400E; width: 28px; height: 28px; line-height: 28px; }
    .trial-banner-close mat-icon { font-size: 16px; }
    .trial-urgent { background: #FEE2E2; border-bottom-color: #FCA5A5; color: #991B1B; }
    .trial-urgent .trial-banner-icon { color: #DC2626; }
    .trial-urgent .trial-upgrade-btn { background: #DC2626 !important; }
    .trial-urgent .trial-banner-close { color: #991B1B; }

    /* ── Offline banner ─────────────────────────────── */
    .offline-banner {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 20px;
      background: #FEF3C7; border-bottom: 1px solid #FCD34D;
      color: #92400E; font-size: 13px; font-weight: 500;
    }
    .offline-banner mat-icon { font-size: 18px; width: 18px; height: 18px; color: #D97706; }

    /* ── Content ────────────────────────────────────── */
    .shell-content {
      display: flex;
      flex-direction: column;
      background: #F1F5F9;
    }
    .page-content {
      padding: 28px 32px;
      flex: 1;
    }

    /* Hamburger — toggles the sidebar open/closed (all sizes) */
    .hamburger-btn { display: inline-flex; color: #64748B; }

    /* ── Responsive ─────────────────────────────────── */

    /* Tablet (< 1024px) */
    @media (max-width: 1023px) {
      .global-search { width: 220px; }
      .page-content { padding: 20px 20px; }
      .user-pill-info { display: none; }
    }

    /* Mobile (< 768px) */
    @media (max-width: 767px) {
      .hamburger-btn { display: inline-flex; }
      .global-search { display: none; }
      .breadcrumb { display: none; }
      .topbar { padding: 0 12px; height: 48px; }
      .page-content { padding: 14px 12px; }
      .topbar-divider { display: none; }
      .user-pill { padding: 4px; }
      .user-pill-info { display: none; }
      .search-shortcut { display: none; }
    }

    /* Landscape phone — maximize content area */
    @media (max-height: 500px) and (orientation: landscape) {
      .hamburger-btn { display: inline-flex; }
      .topbar { height: 36px; }
      .topbar .logo-text { font-size: 14px; }
      .page-content { padding: 8px 12px; }
      .version-chip { display: none; }
      .global-search { display: none; }
      .breadcrumb { display: none; }
    }
  `],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('sidenav') sidenav!: MatSidenav;
  @ViewChild('searchInput') searchInputEl!: any;

  appVersion = APP_VERSION;
  searchQuery = '';
  searchFocused = false;
  searchLoading = false;
  searchHighlight = 0;
  searchResults: { title: string; subtitle: string; icon: string; category: string; categoryLabel: string; route: string[] }[] = [];
  private search$ = new Subject<string>();

  pendingTimesheetCount = 0;
  openBroadcastCount = 0;

  notifPanelOpen = false;
  showTrialBanner = false;
  trialDaysLeft = 0;
  trialEndDate = '';
  mfaEnabled = false;
  currentPageName = 'Today';
  isOffline = !navigator.onLine;
  sidenavMode: 'side' | 'over' = 'side';
  sidenavOpened = true;
  sidenavCollapsed = localStorage.getItem('sidenavCollapsed') === '1';
  private mobileBreakpoint = 768;
  private routeSub?: Subscription;
  private authSub?: Subscription;
  private loggedInInit = false;

  private readonly routeNames: Record<string, string> = {
    '/dashboard': 'Today',
    '/clients': 'Clients',
    '/employees': 'Employees',
    '/providers': 'Providers',
    '/case-managers': 'Case Managers',
    '/service-orders': 'Service Orders',
    '/schedules/builder': 'Schedule Builder',
    '/schedules/monitor': 'Schedule Monitor',
    '/absences': 'Absences',
    '/time-slips/timesheets': 'Timesheets',
    '/time-slips/overtime': 'Overtime Monitor',
    '/time-slips/finish-week': 'Finish the Week',
    '/reports': 'Reports',
    '/ai-skills': 'AI Skills',
    '/account': 'Account',
    '/audit-log': 'Audit Log',
    '/settings': 'Settings',
    '/users': 'Users',
    '/profile': 'My Profile',
  };

  get userInitials(): string {
    const name = this.auth.currentUser?.name ?? '';
    return name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
  }

  constructor(
    public auth: AuthService,
    public tenant: TenantService,
    public features: FeaturesService,
    public notifSvc: NotificationsService,
    private api: ApiService,
    private lifecycle: LifecycleService,
    private clientsSvc: ClientsService,
    private employeesSvc: EmployeesService,
    private soSvc: ServiceOrdersService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.updateSidenavMode();

    this.search$.pipe(
      debounceTime(300),
      switchMap(q => {
        if (q.length < 2) return of({ clients: [], employees: [], sos: [] });
        this.searchLoading = true;
        return forkJoin({
          clients: this.clientsSvc.getAll(q),
          employees: this.employeesSvc.getAll(q),
          sos: this.soSvc.getAll(q),
        });
      }),
    ).subscribe(({ clients, employees, sos }) => {
      const results: typeof this.searchResults = [];
      for (const c of clients.slice(0, 5)) {
        results.push({
          title: `${c.lastName}, ${c.firstName}`,
          subtitle: c.status || 'Client',
          icon: 'people', category: 'client', categoryLabel: 'Client',
          route: ['/clients', String(c.id), 'edit'],
        });
      }
      for (const e of employees.slice(0, 5)) {
        results.push({
          title: `${e.lastName}, ${e.firstName}`,
          subtitle: e.status || 'Employee',
          icon: 'badge', category: 'employee', categoryLabel: 'Employee',
          route: ['/employees', String(e.id), 'edit'],
        });
      }
      for (const so of sos.slice(0, 5)) {
        const clientName = so.client ? `${so.client.lastName}, ${so.client.firstName}` : '';
        results.push({
          title: `SO #${so.serviceOrderNumber}`,
          subtitle: clientName ? `${clientName} · ${so.status}` : so.status || '',
          icon: 'assignment', category: 'so', categoryLabel: 'Service Order',
          route: ['/service-orders', String(so.id)],
        });
      }
      this.searchResults = results;
      this.searchHighlight = 0;
      this.searchLoading = false;
    });

    // Drive logged-in initialization off the auth state, not a one-time ngOnInit
    // check — currentUser$ emits on login AND logout, so this runs immediately on
    // first login (no refresh needed) and tears down cleanly on logout.
    this.authSub = this.auth.currentUser$.subscribe(user => {
      if (user) this.initLoggedInState();
      else this.resetLoggedInState();
    });

    this.routeSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        const path = e.urlAfterRedirects || e.url;
        const match = Object.keys(this.routeNames)
          .sort((a, b) => b.length - a.length)
          .find(k => path.startsWith(k));
        this.currentPageName = match ? this.routeNames[match] : this.resolvePageName(path);
        if (this.sidenavMode === 'over') {
          this.sidenavOpened = false;
        }
      });

  }

  private initLoggedInState() {
    if (this.loggedInInit) return;
    this.loggedInInit = true;

    this.features.getFeatures().subscribe();
    this.auth.getMfaStatus().subscribe(s => { this.mfaEnabled = s.enabled; });
    this.notifSvc.startPolling();
    this.api.get<{ count: number }>('schedules/pending-timesheet-count').subscribe({
      next: r => { this.pendingTimesheetCount = r.count; },
      error: () => {}, // 403 for Read-only users — no badge, no error toast
    });
    this.api.get<{ count: number }>('shift-broadcasts/open-count').subscribe({
      next: r => { this.openBroadcastCount = r.count; },
      error: () => {},
    });

    this.lifecycle.getStatus().subscribe(status => {
      if (status.billingStatus === 'trial_active' && status.trialEndsAt) {
        const trialEnd = new Date(status.trialEndsAt);
        this.trialDaysLeft = Math.max(0, Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
        this.trialEndDate = trialEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        const dismissed = sessionStorage.getItem('trial-banner-dismissed');
        this.showTrialBanner = !dismissed || this.trialDaysLeft <= 3;
      }
    });
  }

  private resetLoggedInState() {
    this.loggedInInit = false;
    this.showTrialBanner = false;
    this.notifSvc.stopPolling();
    this.lifecycle.invalidate();
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
    this.authSub?.unsubscribe();
  }

  @HostListener('window:online')
  onOnline() { this.isOffline = false; }

  @HostListener('window:offline')
  onOffline() { this.isOffline = true; }

  @HostListener('window:resize')
  onResize() {
    this.updateSidenavMode();
  }

  toggleSidenav() {
    // Desktop: collapse to an icon-only rail. Mobile (overlay): open/close.
    if (this.sidenavMode === 'over') {
      this.sidenavOpened = !this.sidenavOpened;
    } else {
      this.sidenavCollapsed = !this.sidenavCollapsed;
      localStorage.setItem('sidenavCollapsed', this.sidenavCollapsed ? '1' : '0');
    }
  }

  // True only when the desktop rail is collapsed — used to enable nav-item tooltips.
  get railCollapsed(): boolean {
    return this.sidenavCollapsed && this.sidenavMode === 'side';
  }

  navClick() {
    if (this.sidenavMode === 'over') {
      this.sidenavOpened = false;
    }
  }

  private updateSidenavMode() {
    const narrowWidth = window.innerWidth < this.mobileBreakpoint;
    const landscapePhone = window.innerHeight < 500 && window.innerWidth < 1024;
    const isMobile = narrowWidth || landscapePhone;
    this.sidenavMode = isMobile ? 'over' : 'side';
    if (!isMobile && !this.sidenavOpened) {
      this.sidenavOpened = true;
    }
  }


  private resolvePageName(path: string): string {
    if (path.includes('/edit') || path.includes('/new')) {
      if (path.startsWith('/clients')) return 'Edit Client';
      if (path.startsWith('/employees')) return 'Edit Employee';
      if (path.startsWith('/service-orders')) return 'Edit Service Order';
      if (path.startsWith('/providers')) return 'Edit Provider';
      if (path.startsWith('/case-managers')) return 'Edit Case Manager';
      if (path.startsWith('/users')) return 'Edit User';
    }
    if (path.startsWith('/reports/')) return 'Report';
    if (path.startsWith('/ai-skills/')) return 'AI Skill';
    return 'Today';
  }

  dismissTrialBanner() {
    this.showTrialBanner = false;
    sessionStorage.setItem('trial-banner-dismissed', '1');
  }

  goToUpgrade() {
    this.router.navigate(['/account'], { queryParams: { tab: 'billing' } });
  }

  toggleNotifPanel(e: Event): void {
    e.stopPropagation();
    this.notifPanelOpen = !this.notifPanelOpen;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.notifPanelOpen) this.notifPanelOpen = false;
  }

  openHelp(): void {
    this.api.get<{ url: string }>('docs/access').subscribe({
      next: ({ url }) => window.open(url, '_blank', 'noopener'),
      error: () => window.open('https://docs.schedulerpro.io', '_blank', 'noopener'),
    });
  }

  @HostListener('window:keydown', ['$event'])
  onGlobalKeydown(e: KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      this.searchInputEl?.nativeElement?.focus();
    }
  }

  onSearchInput(e: Event) {
    this.searchQuery = (e.target as HTMLInputElement).value;
    this.search$.next(this.searchQuery);
  }

  clearSearch() {
    this.searchQuery = '';
    this.searchResults = [];
    this.searchFocused = false;
    this.searchHighlight = 0;
  }

  goToResult(r: typeof this.searchResults[0]) {
    this.clearSearch();
    this.router.navigate(r.route);
  }

  onArrowDown(e: Event) {
    e.preventDefault();
    if (this.searchHighlight < this.searchResults.length - 1) this.searchHighlight++;
  }

  onArrowUp(e: Event) {
    e.preventDefault();
    if (this.searchHighlight > 0) this.searchHighlight--;
  }

  onSearchEnter() {
    if (this.searchResults.length > 0) {
      this.goToResult(this.searchResults[this.searchHighlight]);
    }
  }
}
